import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import { Warband, WarbandLevelUp } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { manageWarbandService } from '../../services/manageWarbandService';
import { getWizardLevelDescription } from '../../utils/formatting';
import { createSubtleIceSurface } from '../../theme/surfaces';

interface ManageLevelsModalProps {
  open: boolean;
  warband: Warband;
  onClose: () => void;
}

type LevelUpMode = 'none' | 'stat' | 'improve' | 'new';

const STAT_OPTIONS = [
  { value: 'F', label: 'Fight' },
  { value: 'S', label: 'Shoot' },
  { value: 'W', label: 'Will' },
  { value: 'H', label: 'Health' },
];

function getMode(lu: WarbandLevelUp): LevelUpMode {
  if (lu.statsIncrease) return 'stat';
  if (lu.spellImprovement) return 'improve';
  if (lu.spellLearned) return 'new';
  return 'none';
}

function computeLevelCount(xp: number): number {
  return Math.floor(xp / 100) + 1;
}

function buildRows(xp: number, warbandId: string, fetched: WarbandLevelUp[]): WarbandLevelUp[] {
  const levelCount = computeLevelCount(xp);
  const rowCount = Math.max(0, levelCount - 1);
  const fetchedMap = new Map(fetched.map((lu) => [lu.level, lu]));
  return Array.from({ length: rowCount }, (_, i) => {
    const level = i + 2;
    return fetchedMap.get(level) ?? {
      warbandId,
      level,
      statsIncrease: null,
      spellImprovement: null,
      spellLearned: null,
    };
  });
}

export const ManageLevelsModal: React.FC<ManageLevelsModalProps> = ({ open, warband, onClose }) => {
  const { setWarbands, warbands, spellcastersAndSpells, setSpellcastersAndSpells } = useWarbandStore();
  const storedLevelUps = useWarbandStore((s) => s.levelUps);
  const setStoreLevelUps = useWarbandStore((s) => s.setLevelUps);
  const allAvailableSpells = useWarbandStore((s) => s.allAvailableSpells);
  const [experience, setExperience] = React.useState(warband.experience);
  const [xpInput, setXpInput] = React.useState(String(warband.experience));
  const [levelUps, setLevelUps] = React.useState<WarbandLevelUp[]>([]);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const originalLevelUpsRef = React.useRef<WarbandLevelUp[]>([]);
  const warbandSpells = spellcastersAndSpells?.warbandSpells ?? [];

  React.useEffect(() => {
    if (!open) return;
    setExperience(warband.experience);
    setXpInput(String(warband.experience));
    setError(null);
    const built = buildRows(warband.experience, warband.id, storedLevelUps);
    setLevelUps(built);
    originalLevelUpsRef.current = built;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExperienceChange = (xp: number) => {
    setExperience(xp);
    const levelCount = computeLevelCount(xp);
    const rowCount = Math.max(0, levelCount - 1);
    setLevelUps((prev) => {
      if (rowCount === prev.length) return prev;
      if (rowCount > prev.length) {
        const extra = Array.from({ length: rowCount - prev.length }, (_, i) => ({
          warbandId: warband.id,
          level: prev.length + i + 2,
          statsIncrease: null,
          spellImprovement: null,
          spellLearned: null,
        }));
        return [...prev, ...extra];
      }
      return prev.slice(0, rowCount);
    });
  };

  const getLevelUpLabel = (lu: WarbandLevelUp): string => {
    if (lu.statsIncrease) {
      const opt = STAT_OPTIONS.find((o) => o.value === lu.statsIncrease);
      return `Stat: ${opt?.label ?? lu.statsIncrease}`;
    }
    if (lu.spellImprovement) return `Improve: ${getSpellName(lu.spellImprovement) ?? lu.spellImprovement}`;
    if (lu.spellLearned) return `New Spell: ${getSpellName(lu.spellLearned) ?? lu.spellLearned}`;
    return '';
  };

  const clearRow = (index: number) => {
    setLevelUps((prev) =>
      prev.map((row, i) =>
        i !== index ? row : { ...row, statsIncrease: null, spellImprovement: null, spellLearned: null }
      )
    );
  };

  const getSpellName = (id: string | null): string | null => {
    if (!id) return null;
    return allAvailableSpells.find((s) => s.id === id)?.name ?? warbandSpells.find((s) => s.id === id)?.name ?? id;
  };

  const handleSave = async () => {
    if (experience < 0) {
      setError('Experience cannot be negative.');
      return;
    }
    setSaveLoading(true);
    setError(null);
    try {
      const rows = levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned);
      await manageWarbandService.saveLevelUps(warband.id, { experience, levelUps: rows });

      // Recompute spell casting numbers in the store from the improvement delta
      const newCounts = new Map<string, number>();
      for (const lu of rows) {
        if (lu.spellImprovement)
          newCounts.set(lu.spellImprovement, (newCounts.get(lu.spellImprovement) ?? 0) + 1);
      }
      const oldCounts = new Map<string, number>();
      for (const lu of originalLevelUpsRef.current) {
        if (lu.spellImprovement)
          oldCounts.set(lu.spellImprovement, (oldCounts.get(lu.spellImprovement) ?? 0) + 1);
      }
      if (spellcastersAndSpells) {
        setSpellcastersAndSpells({
          ...spellcastersAndSpells,
          warbandSpells: spellcastersAndSpells.warbandSpells.map((spell) => {
            const delta = (newCounts.get(spell.id) ?? 0) - (oldCounts.get(spell.id) ?? 0);
            return delta !== 0 ? { ...spell, castingNumber: spell.castingNumber - delta } : spell;
          }),
        });
      }

      setStoreLevelUps(rows);
      setWarbands(warbands.map((w) => (w.id === warband.id ? { ...w, experience } : w)));
      onClose();
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const currentLevel = computeLevelCount(experience);

  return (
    <Dialog open={open} onClose={saveLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Levels</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Experience (XP)"
          type="number"
          value={xpInput}
          onChange={(e) => setXpInput(e.target.value)}
          onBlur={() => {
            const parsed = Math.max(0, parseInt(xpInput, 10) || 0);
            setXpInput(String(parsed));
            handleExperienceChange(parsed);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const parsed = Math.max(0, parseInt(xpInput, 10) || 0);
              setXpInput(String(parsed));
              handleExperienceChange(parsed);
              (e.target as HTMLInputElement).blur();
            }
          }}
          fullWidth
          sx={{ mt: 1 }}
          slotProps={{ htmlInput: { min: 0 } }}
        />
        <Typography variant="body2" color="text.secondary">
          {getWizardLevelDescription({ ...warband, experience })}
        </Typography>
        {warband.apprenticePromotions > 0 && (
          <Typography variant="body2" color="text.secondary">
            Apprentice promotion penalty: &minus;{warband.apprenticePromotions * 6} levels ({warband.apprenticePromotions} &times; 6)
          </Typography>
        )}

        <Accordion
            disableGutters
            defaultExpanded={false}
            sx={{ ...createSubtleIceSurface(500, {}) }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                Level Choices
                {levelUps.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({levelUps.filter((lu) => lu.statsIncrease || lu.spellImprovement || lu.spellLearned).length}/{levelUps.length} recorded)
                  </Typography>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {levelUps.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No level-ups earned yet (level {currentLevel - 1}).
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {levelUps.map((lu, i) => {
                    const mode = getMode(lu);
                    return (
                      <Box key={lu.level} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Stored levels start at 2; wizards display from level 0. */}
                          <Typography variant="subtitle2" sx={{ minWidth: 72 }}>Level {lu.level - 1}</Typography>
                          {mode !== 'none' ? (
                            <>
                              <Chip label={getLevelUpLabel(lu)} size="small" />
                              <IconButton size="small" onClick={() => clearRow(i)}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </Box>
                        {i < levelUps.length - 1 && <Divider sx={{ mt: 1 }} />}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saveLoading}>
          {saveLoading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>

    </Dialog>
  );
};
